// From the field measurements to the production plan.
//
// The rep sketches the room roughly (a template or taps), measures every wall with a laser or
// tape, and measures the diagonals the app asks for. Angles are never assumed: corners are
// placed one by one, each from two measured distances to corners already placed (a triangle),
// so the walls plus n - 3 diagonals fix the shape exactly and every wall length is kept to the
// micrometre. Diagonals beyond those are checks: their disagreement with the plan is how far
// the room is from closing (the server refuses more than ±5 mm). The sketch only decides on
// which side of a known edge each new corner lies.

import { type Pt, diagonalInside, dist, interiorAngles, sketchArea } from "./geometry";
import { got, nth } from "../lib/at";

export interface SolveEdge {
  a: number;
  b: number;
  um: number;
  kind: "wall" | "diagonal";
  /** wall index or diagonal index */
  index: number;
}

export interface SolveInput {
  /** Rough corner positions, clockwise (x east, y north), any unit. */
  sketch: readonly Pt[];
  /** Measured wall lengths in µm, wall i joins corner i and i + 1; null = not measured. */
  walls: readonly (number | null)[];
  diagonals: readonly { a: number; b: number; um: number | null }[];
}

export interface CheckResult {
  edge: SolveEdge;
  /** measured minus plan, µm */
  delta: number;
}

export type SolveResult =
  | {
      ok: true;
      at: [number, number][];
      checks: CheckResult[];
      angles: number[];
    }
  | { ok: false; reason: "missing_walls"; walls: number[] }
  | { ok: false; reason: "not_rigid"; placed: number[]; suggest: [number, number] | null }
  | { ok: false; reason: "impossible_triangle"; corner: number; edges: SolveEdge[] }
  | { ok: false; reason: "bad_sketch" };

/** Triangles thinner than this (sine of the angle at the new corner) are a poor fix. */
const THIN = Math.sin((8 * Math.PI) / 180);

export function solveRoom(input: SolveInput): SolveResult {
  const n = input.sketch.length;
  if (n < 3 || input.walls.length !== n) return { ok: false, reason: "bad_sketch" };
  const missing = input.walls.flatMap((w, i) => (w === null || w <= 0 ? [i] : []));
  if (missing.length) return { ok: false, reason: "missing_walls", walls: missing };

  const edges: SolveEdge[] = input.walls.map((um, i) => ({
    a: i,
    b: (i + 1) % n,
    um: um as number,
    kind: "wall",
    index: i,
  }));
  input.diagonals.forEach((d, i) => {
    if (d.um !== null && d.um > 0 && d.a !== d.b) edges.push({ a: d.a, b: d.b, um: d.um, kind: "diagonal", index: i });
  });

  const sketch = input.sketch;
  const pos = new Map<number, [number, number]>();
  const used = new Set<SolveEdge>();

  // Corner 0 at the origin, wall 0 in its sketched direction (the plan looks like the sketch).
  const s0 = nth(sketch, 0);
  const s1 = nth(sketch, 1);
  const theta = Math.atan2(s1[1] - s0[1], s1[0] - s0[0]);
  const wall0 = nth(edges, 0);
  pos.set(0, [0, 0]);
  pos.set(1, [wall0.um * Math.cos(theta), wall0.um * Math.sin(theta)]);
  used.add(wall0);

  const edgesOf = (c: number) => edges.filter((e) => e.a === c || e.b === c);
  const other = (e: SolveEdge, c: number) => (e.a === c ? e.b : e.a);

  while (pos.size < n) {
    let best: { c: number; e1: SolveEdge; e2: SolveEdge; score: number } | null = null;
    for (let c = 0; c < n; c++) {
      if (pos.has(c)) continue;
      const known = edgesOf(c).filter((e) => pos.has(other(e, c)));
      for (let i = 0; i < known.length; i++) {
        for (let j = i + 1; j < known.length; j++) {
          const e1 = nth(known, i);
          const e2 = nth(known, j);
          const p = other(e1, c);
          const q = other(e2, c);
          if (p === q) continue;
          // Prefer walls (so every wall length is used to fix the plan), then well-shaped
          // triangles, judged on the sketch.
          const walls = (e1.kind === "wall" ? 1 : 0) + (e2.kind === "wall" ? 1 : 0);
          const sp = nth(sketch, p);
          const sq = nth(sketch, q);
          const sc = nth(sketch, c);
          const u = [sp[0] - sc[0], sp[1] - sc[1]];
          const v = [sq[0] - sc[0], sq[1] - sc[1]];
          const sin = Math.abs(nth(u, 0) * nth(v, 1) - nth(u, 1) * nth(v, 0)) / (Math.hypot(nth(u, 0), nth(u, 1)) * Math.hypot(nth(v, 0), nth(v, 1)) || 1);
          const score = walls * 10 + (sin >= THIN ? 5 : 0) + sin;
          if (!best || score > best.score) best = { c, e1, e2, score };
        }
      }
    }
    if (!best) {
      return { ok: false, reason: "not_rigid", placed: [...pos.keys()].sort((a, b) => a - b), suggest: suggestDiagonal(sketch, pos, edges) };
    }
    const { c, e1, e2 } = best;
    const p = other(e1, c);
    const q = other(e2, c);
    const placed = place(got(pos, p), got(pos, q), e1.um, e2.um, sideOf(nth(sketch, p), nth(sketch, q), nth(sketch, c)));
    if (!placed) return { ok: false, reason: "impossible_triangle", corner: c, edges: [e1, e2, ...edgeBetween(edges, p, q)] };
    pos.set(c, placed);
    used.add(e1);
    used.add(e2);
  }

  const at = Array.from({ length: n }, (_, i) => {
    const p = got(pos, i);
    return [Math.round(p[0]), Math.round(p[1])] as [number, number];
  });
  const checks = edges
    .filter((e) => !used.has(e))
    .map((edge) => ({ edge, delta: edge.um - dist(nth(at, edge.a), nth(at, edge.b)) }));
  return { ok: true, at, checks, angles: interiorAngles(at) };
}

function edgeBetween(edges: SolveEdge[], p: number, q: number): SolveEdge[] {
  return edges.filter((e) => (e.a === p && e.b === q) || (e.a === q && e.b === p));
}

/** Which side of p→q the sketch puts c: 1 left, -1 right. */
function sideOf(p: Pt, q: Pt, c: Pt): 1 | -1 {
  const v = (q[0] - p[0]) * (c[1] - p[1]) - (q[1] - p[1]) * (c[0] - p[0]);
  return v >= 0 ? 1 : -1;
}

/** The corner at distance r1 from p and r2 from q on the given side of p→q. */
function place(p: Pt, q: Pt, r1: number, r2: number, side: 1 | -1): [number, number] | null {
  const d = dist(p, q);
  if (d === 0) return null;
  const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
  let h2 = r1 * r1 - a * a;
  if (h2 < 0) {
    // A measured triangle that does not close. Allow rounding noise (a few µm) only.
    if (h2 < -(25 * d)) return null;
    h2 = 0;
  }
  const h = Math.sqrt(h2);
  const ux = (q[0] - p[0]) / d;
  const uy = (q[1] - p[1]) / d;
  // Left of p→q is the direction (-uy, ux).
  return [p[0] + a * ux - side * h * uy, p[1] + a * uy + side * h * ux];
}

/** A diagonal from a placed corner to an unplaced one that lies inside the sketched room. */
function suggestDiagonal(sketch: readonly Pt[], pos: Map<number, unknown>, edges: SolveEdge[]): [number, number] | null {
  const n = sketch.length;
  const measured = (a: number, b: number) => edges.some((e) => (e.a === a && e.b === b) || (e.a === b && e.b === a));
  let best: [number, number] | null = null;
  let bestLen = Infinity;
  for (let u = 0; u < n; u++) {
    if (pos.has(u)) continue;
    // Prefer unplaced corners that already hang on the placed part by one wall.
    const hangs = pos.has((u + 1) % n) || pos.has((u - 1 + n) % n);
    for (const p of pos.keys()) {
      if (measured(p, u) || !diagonalInside(sketch, p, u)) continue;
      const len = dist(nth(sketch, p), nth(sketch, u)) * (hangs ? 1 : 10);
      if (len < bestLen) {
        bestLen = len;
        best = [Math.min(p, u), Math.max(p, u)];
      }
    }
  }
  return best;
}

export interface DiagonalPlan {
  /** n - 3 diagonals that fix every angle */
  fix: [number, number][];
  /** one more that checks the room closes */
  check: [number, number] | null;
}

/**
 * Which diagonals to measure for a sketched room: a fan from one corner that sees every other
 * corner (so the rep measures from one spot), or an ear-clipping triangulation for rooms no
 * single corner sees; plus one crossing diagonal as the closure check.
 */
export function planDiagonals(sketch: readonly Pt[]): DiagonalPlan {
  const n = sketch.length;
  if (n < 4) return { fix: [], check: null };
  let fix: [number, number][] | null = null;
  for (let apex = 0; apex < n && !fix; apex++) {
    const fan: [number, number][] = [];
    for (let k = 2; k <= n - 2; k++) fan.push(norm(apex, (apex + k) % n));
    if (fan.every(([a, b]) => diagonalInside(sketch, a, b))) fix = fan;
  }
  fix ??= earClip(sketch);
  const taken = new Set(fix.map(([a, b]) => `${a}-${b}`));
  let check: [number, number] | null = null;
  let bestLen = -1;
  for (let a = 0; a < n; a++) {
    for (let b = a + 2; b < n; b++) {
      if (a === 0 && b === n - 1) continue;
      if (taken.has(`${a}-${b}`) || !diagonalInside(sketch, a, b)) continue;
      // The longest one checks the most of the room.
      const len = dist(nth(sketch, a), nth(sketch, b));
      if (len > bestLen) {
        bestLen = len;
        check = [a, b];
      }
    }
  }
  return { fix, check };
}

function norm(a: number, b: number): [number, number] {
  return a < b ? [a, b] : [b, a];
}

function earClip(sketch: readonly Pt[]): [number, number][] {
  const idx = sketch.map((_, i) => i);
  const out: [number, number][] = [];
  const cw = sketchArea(sketch) < 0;
  let guard = 0;
  while (idx.length > 3 && guard++ < 10_000) {
    let clipped = false;
    for (let k = 0; k < idx.length; k++) {
      const prev = nth(idx, (k - 1 + idx.length) % idx.length);
      const cur = nth(idx, k);
      const next = nth(idx, (k + 1) % idx.length);
      const a = nth(sketch, prev);
      const b = nth(sketch, cur);
      const c = nth(sketch, next);
      const cross = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
      const convex = cw ? cross < 0 : cross > 0;
      if (!convex) continue;
      const sub = idx.map((i) => nth(sketch, i));
      const pi = (k - 1 + idx.length) % idx.length;
      const ni = (k + 1) % idx.length;
      if (!diagonalInside(sub, pi, ni)) continue;
      out.push(norm(prev, next));
      idx.splice(k, 1);
      clipped = true;
      break;
    }
    if (!clipped) break;
  }
  return out;
}
